/**
 * useTenantReps — fetch the live owner roster for the current tenant.
 *
 * W33 fix: expanded role filter from role='ae' only to include 'manager' and
 * 'executive' so that Bryan K. Short (role='executive') appears in the Owner
 * picker on /new-account and account reassignment flows.
 *
 * Queries Supabase users where role IN ('ae','manager','executive'),
 * tenant-scoped via getCurrentTenant().
 * Module-scoped cache keyed by tenant_id avoids refetching on every mount.
 *
 * Returns { reps, loading, error }. Each rep is { id, name, email }.
 */
import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";

export interface TenantRep {
  id: string;
  name: string;
  email: string;
}

interface CacheEntry {
  reps: TenantRep[];
  fetchedAt: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const _cache = new Map<number, CacheEntry>();
const _inflight = new Map<number, Promise<TenantRep[]>>();

async function fetchReps(tenantId: number): Promise<TenantRep[]> {
  const existing = _inflight.get(tenantId);
  if (existing) return existing;

  const p = (async () => {
    const sb = supabase();
    const { data, error } = await sb
      .from("users")
      .select("id, full_name, email")
      .eq("tenant_id", tenantId)
      .in("role", ["ae", "manager", "executive"])
      .order("full_name", { ascending: true });
    if (error) throw error;
    const reps: TenantRep[] = (data ?? []).map((row: any) => ({
      id: String(row.id),
      name: row.full_name ?? row.email ?? "Unnamed",
      email: row.email ?? "",
    }));
    _cache.set(tenantId, { reps, fetchedAt: Date.now() });
    return reps;
  })();

  _inflight.set(tenantId, p);
  try {
    return await p;
  } finally {
    _inflight.delete(tenantId);
  }
}

export function useTenantReps() {
  const tenant = getCurrentTenant();
  const tenantId = tenant?.id ?? null;

  const cached = tenantId != null ? _cache.get(tenantId) : null;
  const fresh = cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS;

  const [reps, setReps] = useState<TenantRep[]>(fresh ? cached!.reps : []);
  const [loading, setLoading] = useState<boolean>(!fresh && tenantId != null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (tenantId == null) {
      setReps([]);
      setLoading(false);
      return;
    }
    const c = _cache.get(tenantId);
    if (c && Date.now() - c.fetchedAt < CACHE_TTL_MS) {
      setReps(c.reps);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchReps(tenantId)
      .then((r) => {
        if (cancelled) return;
        setReps(r);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setReps([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return { reps, loading, error };
}

export function clearTenantRepsCache(tenantId?: number) {
  if (tenantId == null) _cache.clear();
  else _cache.delete(tenantId);
}
